<template lang="html">
    <div class="hydrologic-variability-tables-pdf">
        <div
            v-for="(firstColumnValues, firstColumn, firstColumnIndex) in data"
            :key="firstColumnIndex"
            class="year-table no-page-break"
            :class="`table-${firstColumnIndex + 1}`"
        >
            <div
                v-for="(percentileValues, percentile, percentileIndex) in dataWithMonths(firstColumnValues)"
                :key="percentileIndex"
                class="columns"
                :class="classObject(percentileIndex)"
            >
                <div
                    class="column percentile bold-text"
                >
                    <span v-if="percentile === 'months'">
                        &nbsp;
                    </span>
                    <!-- replace 50th percentile with 'Mean' -->
                    <span v-else-if="percentile === '50th' && meanData">
                        Mean
                    </span>
                    <span v-else-if="percentile === 'candidates'">
                        Watershed
                    </span>
                    <span v-else>
                        {{ percentile }}
                    </span>
                </div>
                <div
                    v-for="(monthlyValues, month, monthIndex) in percentileValues"
                    :key="monthIndex"
                    class="column month-value"
                >
                    <span
                        v-if="percentileIndex === 0"
                        class="bold-text"
                    >
                        {{ monthNumberToName(month + 1) }}
                    </span>
                    <!-- replace 50th percentile data with 'Mean' data -->
                    <span v-else-if="percentileIndex === 3 && meanData">
                        {{ meanData[monthIndex] | numFormat('0.00') }}
                    </span>
                    <span v-else-if="percentile === 'candidates'">
                        {{ monthlyValues }}
                    </span>
                    <span v-else>
                        {{ monthlyValues | numFormat('0.00') }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { monthNumberToName } from './helpers';

export default {
    name: 'HydrologicVariabilityTable',

    props: {
        data: {
            type: Object,
            required: true,
        },

        hasCandidates: {
            type: Boolean,
            required: false,
            default: false,
        },

        meanData: {
            type: Array,
            required: false,
            default: null,
        },
    },

    data: () => ({
        // create array with 12 values for months
        months: [...Array(12).keys()],
    }),

    methods: {
        /**
         * Combines months with period data for table formatting
         * @param {Object} data - period data for date range
         * @returns {Object} combined object containing months and data
         */
        dataWithMonths(data) {
            return { months: this.months, ...data };
        },

        /**
         * adds conditional css classes to change row highlighting color
         * @param {Number} percentileIndex Current index of percentile from 'percs' array
         */
        classObject(percentileIndex) {
            return {
                'months-row': percentileIndex === 0,
                'candidate-row': this.hasCandidates && percentileIndex === 1,
                // changes the background color to a lighter color for the 90th, and 10th percentiles.
                accent: (this.hasCandidates ? [2, 6] : [1, 5]).includes(percentileIndex),
            };
        },

        monthNumberToName,
    },
};
</script>

<style lang="scss">
.hydrologic-variability-tables-pdf {
    $light-grey-accent: #e6e6e6 !default;
    $color-hydrovar-mid-value: #ccc !default;
    $color-hydrovar-1: #32429b !default;
    $color-hydrovar-1-accent: #7a85c1 !default;
    $color-hydrovar-2: #8f3d96 !default;
    $color-hydrovar-2-accent: #c694c3 !default;
    $color-hydrovar-3: #418ecc !default;
    $color-hydrovar-3-accent: #95c8ec !default;
    $color-hydrovar-4: #1abcd4 !default;
    $color-hydrovar-4-accent: #88d3e2 !default;
    $color-hydrovar-candidate-row: #b1b1b1 !default;

    .year-table {
        font-size: 10px;
        margin-bottom: 2rem;
        color: #fff;

        .bold-text {
            font-size: 11px;
            font-weight: bold;
        }

        // year range colors
        &.table-1 {
            background: $color-hydrovar-1;

            .accent {
                background: $color-hydrovar-1-accent;
            }
        }

        &.table-2 {
            background: $color-hydrovar-2;

            .accent {
                background: $color-hydrovar-2-accent;
            }
        }

        &.table-3 {
            background: $color-hydrovar-3;

            .accent {
                background: $color-hydrovar-3-accent;
            }
        }

        &.table-4 {
            background: $color-hydrovar-4;

            .accent {
                background: $color-hydrovar-4-accent;
            }
        }

        .months-row {
            background: darken($color-hydrovar-candidate-row, 10%);
        }

        .candidate-row {
            background: $color-hydrovar-candidate-row;

            .column:first-child span {
                font-size: 0.75em;
            }
        }

        .columns {
            margin: 0;

            .column {
                padding: 0.3em;
                border: 1px solid $light-grey-accent;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }
}

</style>
